import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Promotions({ user }) {
  const [activeTab, setActiveTab] = useState('eligibility');
  const [personnel, setPersonnel] = useState([]);
  const [promotionRequests, setPromotionRequests] = useState([]);
  const [eligibilityData, setEligibilityData] = useState({});
  const [ranks, setRanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecommendModal, setShowRecommendModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [recommendForm, setRecommendForm] = useState({
    user_id: '',
    requested_rank_id: '',
    supervisor_recommendation: 'Recommend',
    supervisor_comments: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [personnelRes, requestsRes, ranksRes] = await Promise.all([
        axios.get(`${API}/personnel`),
        axios.get(`${API}/promotion-requests`),
        axios.get(`${API}/ranks`),
      ]);
      setPersonnel(personnelRes.data);
      setPromotionRequests(requestsRes.data);
      setRanks(ranksRes.data);
      
      // Fetch eligibility for each personnel
      const eligibility = {};
      for (const person of personnelRes.data) {
        try {
          const res = await axios.get(`${API}/promotions/eligibility/${person.id}`);
          eligibility[person.id] = res.data;
        } catch (err) {
          // Skip if eligibility check fails
        }
      }
      setEligibilityData(eligibility);
    } catch (error) {
      console.error('Failed to fetch promotion data:', error);
      setError(error.response?.data?.detail || 'Failed to load promotion data');
    } finally {
      setLoading(false);
    }
  };

  const handleRecommend = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/promotions/recommend`, recommendForm);
      setSuccess('Promotion recommendation submitted successfully!');
      setShowRecommendModal(false);
      setRecommendForm({
        user_id: '',
        requested_rank_id: '',
        supervisor_recommendation: 'Recommend',
        supervisor_comments: '',
      });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit recommendation');
    }
  };

  const handleReview = async (requestId, decision, comments) => {
    try {
      await axios.put(`${API}/promotion-requests/${requestId}/review`, {
        decision,
        review_comments: comments,
      });
      setSuccess(`Promotion ${decision.toLowerCase()} successfully!`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to review promotion');
    }
  };

  const getEligibilityColor = (score) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-amber-400';
    return 'text-red-400';
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Pending': 'bg-amber-900/50 text-amber-300 border-amber-700',
      'Approved': 'bg-emerald-900/50 text-emerald-300 border-emerald-700',
      'Rejected': 'bg-red-900/50 text-red-300 border-red-700',
    };
    return styles[status] || 'bg-slate-900/50 text-slate-300 border-slate-700';
  };

  const openRecommendModal = (person) => {
    const eligibility = eligibilityData[person.id];
    if (eligibility && eligibility.next_rank_id) {
      setSelectedPerson(person);
      setRecommendForm({
        ...recommendForm,
        user_id: person.id,
        requested_rank_id: eligibility.next_rank_id,
      });
      setShowRecommendModal(true);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="promotions-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Rank & Promotions</h1>
          <p className="text-slate-400">Promotion eligibility, recommendations, and approvals</p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-900/30 border border-emerald-700 text-emerald-300 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="military-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Eligible for Promotion</p>
              <p className="text-2xl font-bold text-emerald-400">
                {Object.values(eligibilityData).filter(e => e?.eligible).length}
              </p>
            </div>
            <div className="w-10 h-10 bg-emerald-600/30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="military-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Pending Requests</p>
              <p className="text-2xl font-bold text-amber-400">
                {promotionRequests.filter(r => r.status === 'Pending').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-amber-600/30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="military-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Approved</p>
              <p className="text-2xl font-bold text-white">
                {promotionRequests.filter(r => r.status === 'Approved').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-600/30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </div>
        <div className="military-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Personnel</p>
              <p className="text-2xl font-bold text-white">{personnel.length}</p>
            </div>
            <div className="w-10 h-10 bg-purple-600/30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-nav">
        <button
          onClick={() => setActiveTab('eligibility')}
          className={`tab-button ${activeTab === 'eligibility' ? 'active' : ''}`}
          data-testid="tab-eligibility"
        >
          Eligibility
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`tab-button ${activeTab === 'requests' ? 'active' : ''}`}
          data-testid="tab-requests"
        >
          Requests ({promotionRequests.length})
        </button>
      </div>

      {/* Eligibility Tab */}
      {activeTab === 'eligibility' && (
        <div className="space-y-4">
          {personnel.map((person) => {
            const eligibility = eligibilityData[person.id];
            if (!eligibility) return null;
            
            return (
              <div key={person.id} className="military-card p-5" data-testid={`eligibility-${person.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-white">{person.full_name}</h3>
                    <p className="text-slate-400 text-sm">{person.service_number}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {eligibility.eligible ? (
                      <span className="px-3 py-1 bg-emerald-900/50 text-emerald-300 text-xs font-semibold rounded border border-emerald-700">
                        ELIGIBLE
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-slate-900/50 text-slate-400 text-xs font-semibold rounded border border-slate-700">
                        NOT ELIGIBLE
                      </span>
                    )}
                    {eligibility.eligible && user.rank?.level >= 3 && eligibility.next_rank_id && (
                      <button
                        onClick={() => openRecommendModal(person)}
                        className="btn-gold text-sm py-1 px-3"
                        data-testid={`recommend-btn-${person.id}`}
                      >
                        Recommend
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-slate-500 text-xs mb-1">Current Rank</p>
                    <p className="text-white font-semibold">{eligibility.current_rank_name}</p>
                  </div>
                  <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-slate-500 text-xs mb-1">Next Rank</p>
                    <p className="text-amber-400 font-semibold">{eligibility.next_rank_name || 'Max Rank'}</p>
                  </div>
                  <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-slate-500 text-xs mb-1">Time in Rank</p>
                    <p className="text-white font-semibold">{eligibility.time_in_rank_days} days</p>
                  </div>
                  <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-slate-500 text-xs mb-1">Eligibility Score</p>
                    <p className={`font-bold text-xl ${getEligibilityColor(eligibility.eligibility_score)}`}>
                      {eligibility.eligibility_score?.toFixed(1)}
                    </p>
                  </div>
                </div>

                {/* Score Breakdown */}
                <div className="border-t border-slate-700 pt-4">
                  <p className="text-slate-400 text-sm mb-3">Score Breakdown</p>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-500">Time in Rank</span>
                        <span className="text-white">{eligibility.time_in_rank_points?.toFixed(1)}/30</span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${(eligibility.time_in_rank_points / 30) * 100}%` }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-500">Training</span>
                        <span className="text-white">{eligibility.training_completion_points?.toFixed(1)}/25</span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${(eligibility.training_completion_points / 25) * 100}%` }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-500">Performance</span>
                        <span className="text-white">{eligibility.deployment_performance_points?.toFixed(1)}/25</span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${(eligibility.deployment_performance_points / 25) * 100}%` }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-500">Command Eval</span>
                        <span className="text-white">{eligibility.command_evaluation_points?.toFixed(1)}/20</span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${(eligibility.command_evaluation_points / 20) * 100}%` }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                {eligibility.notes && (
                  <p className="text-slate-500 text-sm mt-3 italic">{eligibility.notes}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div className="space-y-4">
          {promotionRequests.length === 0 ? (
            <div className="text-center py-12 text-slate-500 military-card">
              No promotion requests found
            </div>
          ) : (
            promotionRequests.map((request) => {
              const person = personnel.find(p => p.id === request.user_id);
              const currentRank = ranks.find(r => r.id === request.current_rank_id);
              const requestedRank = ranks.find(r => r.id === request.requested_rank_id);
              
              return (
                <div key={request.id} className="military-card p-5" data-testid={`request-${request.id}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-white">{person?.full_name || 'Unknown'}</h3>
                      <p className="text-slate-400 text-sm">
                        {currentRank?.name} → <span className="text-amber-400">{requestedRank?.name}</span>
                      </p>
                    </div>
                    <span className={`px-3 py-1 text-xs font-semibold rounded border ${getStatusBadge(request.status)}`}>
                      {request.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-slate-500 text-xs">Recommendation</p>
                      <p className="text-white font-medium">{request.supervisor_recommendation}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Eligibility Score</p>
                      <p className={`font-bold ${getEligibilityColor(request.eligibility_score)}`}>
                        {request.eligibility_score?.toFixed(1)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Time in Rank</p>
                      <p className="text-white">{request.time_in_rank_days} days</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Request Date</p>
                      <p className="text-white">{new Date(request.request_date).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {request.supervisor_comments && (
                    <div className="bg-slate-800/50 rounded-lg p-3 mb-4">
                      <p className="text-slate-500 text-xs mb-1">Supervisor Comments</p>
                      <p className="text-slate-300 text-sm">{request.supervisor_comments}</p>
                    </div>
                  )}

                  {request.status === 'Pending' && user.rank?.level >= 4 && (
                    <div className="flex gap-3 pt-4 border-t border-slate-700">
                      <button
                        onClick={() => handleReview(request.id, 'Approved', 'Promotion approved based on merit and eligibility.')}
                        className="btn-gold flex-1"
                        data-testid={`approve-btn-${request.id}`}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleReview(request.id, 'Rejected', 'Promotion denied at this time.')}
                        className="btn-military flex-1"
                        data-testid={`reject-btn-${request.id}`}
                      >
                        Reject
                      </button>
                    </div>
                  )}

                  {request.review_comments && (
                    <div className="mt-4 pt-4 border-t border-slate-700">
                      <p className="text-slate-500 text-xs mb-1">Review Comments</p>
                      <p className="text-slate-300 text-sm">{request.review_comments}</p>
                      {request.review_date && (
                        <p className="text-slate-500 text-xs mt-2">
                          Reviewed: {new Date(request.review_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Recommend Modal */}
      {showRecommendModal && selectedPerson && (
        <div className="modal-overlay" data-testid="recommend-modal">
          <div className="modal-content p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Recommend Promotion</h2>
              <button onClick={() => setShowRecommendModal(false)} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
              <p className="text-white font-medium">{selectedPerson.full_name}</p>
              <p className="text-slate-400 text-sm">
                {eligibilityData[selectedPerson.id]?.current_rank_name} → 
                <span className="text-amber-400 ml-1">{eligibilityData[selectedPerson.id]?.next_rank_name}</span>
              </p>
              <p className="text-emerald-400 text-sm mt-1">
                Eligibility Score: {eligibilityData[selectedPerson.id]?.eligibility_score?.toFixed(1)}
              </p>
            </div>

            <form onSubmit={handleRecommend} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Recommendation</label>
                <select
                  value={recommendForm.supervisor_recommendation}
                  onChange={(e) => setRecommendForm({ ...recommendForm, supervisor_recommendation: e.target.value })}
                  className="input-military"
                  data-testid="recommendation-select"
                >
                  <option value="Strongly Recommend">Strongly Recommend</option>
                  <option value="Recommend">Recommend</option>
                  <option value="Neutral">Neutral</option>
                  <option value="Not Recommend">Not Recommend</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Comments</label>
                <textarea
                  value={recommendForm.supervisor_comments}
                  onChange={(e) => setRecommendForm({ ...recommendForm, supervisor_comments: e.target.value })}
                  required
                  className="input-military h-32 resize-none"
                  placeholder="Provide justification for this recommendation..."
                  data-testid="recommendation-comments-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowRecommendModal(false)} className="btn-military">
                  Cancel
                </button>
                <button type="submit" className="btn-gold" data-testid="submit-recommendation">
                  Submit Recommendation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Promotions;
